import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import { ICustomReportTemplateListItem } from '@/Interfaces/ReportBuilder/Template.interface';
import { deleteReportTemplate, fetchAllReportTemplates, shareReportTemplate } from '@/store/api/reportBuilder.api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

export function useTemplateReports(channel: CustomReportChannel) {
  const [allTemplates, setAllTemplates] = useState<ICustomReportTemplateListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [shareTargetId, setShareTargetId] = useState<number | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllReportTemplates();
      setAllTemplates(data);
    } catch {
      toast.error('Failed to load report templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(
    () => allTemplates.filter(template => template.channel === channel),
    [allTemplates, channel],
  );

  const onShareTemplate = useCallback((template: ICustomReportTemplateListItem) => {
    setShareTargetId(template.id);
  }, []);

  const onShareModalClose = useCallback(() => {
    setShareTargetId(null);
  }, []);

  const onSubmitShare = useCallback(
    async ({
      receivingUserIds,
      shareWithOrg,
      targetId,
    }: { receivingUserIds: number[]; shareWithOrg: boolean; targetId: number }) => {
      try {
        await shareReportTemplate(targetId, { receivingUserIds, shareWithOrg });
        toast.success('Template successfully shared.');
      } catch {
        toast.error('Something went wrong on sharing attempt.');
      } finally {
        setShareTargetId(null);
      }
    },
    [],
  );

  const onDeleteTemplate = useCallback(
    async (template: ICustomReportTemplateListItem) => {
      try {
        await deleteReportTemplate(template.id, template.channel);
        toast.success('Template deleted.');
        await loadTemplates();
      } catch {
        toast.error('Failed to delete template.');
      }
    },
    [loadTemplates],
  );

  return {
    templates: filteredTemplates,
    loading,
    shareTargetId,
    onShareTemplate,
    onShareModalClose,
    onSubmitShare,
    onDeleteTemplate,
    reloadTemplates: loadTemplates,
  };
}
